import { zodResolver } from "@hookform/resolvers/zod";
import type React from "react";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useAuth } from "../context/AuthContext";
import {
	type WhitelistingFormData,
	whitelistingSchema,
} from "../schemas/whitelisting";
import axiosClient, { ApiError } from "../services/api/axiosClient";
import type { Wallet } from "../types";
import { notifications } from "../utils/notifications";
import FieldError from "./FieldError";
import ValidationBanner from "./ValidationBanner";
import "./WhitelistingForm.css";

interface WhitelistingFormProps {
	wallet: Wallet;
	isVisible: boolean;
	onSuccess: (environments: string[]) => void;
	onCancel: () => void;
}

// Test data constants (DEV only - for local testing)
const TEST_DATA_CUSTODIAL: WhitelistingFormData = {
	walletType: "custodial",
	partnerName: "Test Custodial Wallet",
	emailAddress: "test@example.com",
	clientDomain: "api.example.com",
	authenticationWalletAccounts:
		"GXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX",
	withdrawWalletAccounts:
		"GXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX",
	depositWalletAccounts:
		"GXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX",
	authAddress: "",
	withdrawAddress: "",
	depositAddress: "",
};

const TEST_DATA_NON_CUSTODIAL: WhitelistingFormData = {
	walletType: "non-custodial",
	partnerName: "Test Non-Custodial Wallet",
	emailAddress: "test@example.com",
	clientDomain: "stellar.example.com",
	authenticationWalletAccounts: "",
	withdrawWalletAccounts: "",
	depositWalletAccounts: "",
	authAddress: "",
	withdrawAddress: "",
	depositAddress: "",
};

const isDev = import.meta.env.DEV;

const WhitelistingForm: React.FC<WhitelistingFormProps> = ({
	wallet,
	isVisible,
	onSuccess,
	onCancel,
}) => {
	const { user } = useAuth();
	const bannerRef = useRef<HTMLDivElement>(null);

	const form = useForm<WhitelistingFormData>({
		resolver: zodResolver(whitelistingSchema),
		defaultValues: {
			walletType:
				wallet.custodial_type === "Custodial" ? "custodial" : "non-custodial",
			partnerName: wallet.wallet_name || "",
			emailAddress: user?.email || wallet.main_contact_email || "",
			clientDomain: wallet.home_domain || "",
			authenticationWalletAccounts: wallet.funding_address || "",
			withdrawWalletAccounts: wallet.funding_address || "",
			depositWalletAccounts: wallet.funding_address || "",
			authAddress: wallet.home_domain || "",
			withdrawAddress: wallet.home_domain || "",
			depositAddress: wallet.home_domain || "",
		},
		mode: "onSubmit",
	});

	useEffect(() => {
		if (isVisible) {
			form.reset({
				walletType:
					wallet.custodial_type === "Custodial" ? "custodial" : "non-custodial",
				partnerName: wallet.wallet_name || "",
				emailAddress: user?.email || wallet.main_contact_email || "",
				clientDomain: wallet.home_domain || "",
				authenticationWalletAccounts: wallet.funding_address || "",
				withdrawWalletAccounts: wallet.funding_address || "",
				depositWalletAccounts: wallet.funding_address || "",
				authAddress: wallet.home_domain || "",
				withdrawAddress: wallet.home_domain || "",
				depositAddress: wallet.home_domain || "",
			});
		}
	}, [isVisible, wallet, user, form]);

	const fillTestData = (type: "custodial" | "non-custodial") => {
		if (!isDev) return;
		const testData =
			type === "custodial" ? TEST_DATA_CUSTODIAL : TEST_DATA_NON_CUSTODIAL;
		form.reset(testData);
		notifications.success(`Filled with ${type} test data`);
	};

	const onSubmit = async (data: WhitelistingFormData) => {
		try {
			const payload = {
				walletType: data.walletType,
				partnerName: data.partnerName,
				emailAddress: data.emailAddress,
				clientDomain: data.clientDomain,
				...(data.walletType === "custodial"
					? {
							authenticationWalletAccounts: data.authenticationWalletAccounts,
							withdrawWalletAccounts: data.withdrawWalletAccounts,
							depositWalletAccounts: data.depositWalletAccounts,
						}
					: {}),
			};

			await axiosClient.post("/api/stellar/register", payload);
			onSuccess(["Sandbox", "Preview"]);
		} catch (err) {
			const errorMsg =
				err instanceof ApiError ? err.message : "Network error occurred";
			form.setError("root", { message: errorMsg });
			notifications.error(`Whitelisting failed: ${errorMsg}`);
		}
	};

	const onInvalid = () => {
		bannerRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	const walletType = form.watch("walletType");
	const errors = form.formState.errors;
	const hasErrors = Object.keys(errors).length > 0;
	const errorMessages = hasErrors
		? Object.values(errors)
				.filter((e): e is { message?: string } => !!e && typeof e === "object")
				.map((e) => e.message)
				.filter((m): m is string => !!m)
		: [];
	const isSubmitting = form.formState.isSubmitting;

	if (!isVisible) return null;

	return (
		<div className="whitelist-form-container">
			<div className="whitelist-form-header">
				{/* Hidden test data triggers - DEV only, click corners to prefill */}
				{isDev && (
					<>
						<button
							type="button"
							className="hidden-trigger hidden-trigger-left"
							onClick={() => fillTestData("non-custodial")}
							title="Click to fill non-custodial test data"
							aria-hidden
						/>
						<button
							type="button"
							className="hidden-trigger hidden-trigger-right"
							onClick={() => fillTestData("custodial")}
							title="Click to fill custodial test data"
							aria-hidden
						/>
					</>
				)}
				<h3>Wallet Whitelisting</h3>
				<p>
					Complete the information below to whitelist your wallet with
					MoneyGram's Stellar Adapter Service.
				</p>
			</div>

			<form
				onSubmit={form.handleSubmit(onSubmit, onInvalid)}
				className="whitelist-form"
			>
				<div ref={bannerRef}>
					{hasErrors && (
						<ValidationBanner
							message="Please fix the errors below before submitting."
							fields={errorMessages}
							aria-live="polite"
						/>
					)}
				</div>

				{/* Wallet Type */}
				<div className="form-row">
					<label htmlFor="walletType">
						Wallet Type <span className="required">*</span>
					</label>
					<select id="walletType" {...form.register("walletType")} disabled>
						<option value="custodial">Custodial</option>
						<option value="non-custodial">Non-Custodial</option>
					</select>
					<small>Based on your wallet configuration</small>
				</div>

				{/* Wallet Name */}
				<div className="form-row">
					<label htmlFor="partnerName">
						Wallet Name <span className="required">*</span>
					</label>
					<input
						type="text"
						id="partnerName"
						{...form.register("partnerName")}
						placeholder="e.g., Your Wallet Name"
						className={errors.partnerName ? "error" : undefined}
						aria-invalid={!!errors.partnerName}
						aria-describedby={
							errors.partnerName ? "partnerName-error" : undefined
						}
					/>
					{errors.partnerName?.message && (
						<FieldError
							id="partnerName-error"
							message={errors.partnerName.message}
						/>
					)}
				</div>

				{/* Email Address */}
				<div className="form-row">
					<label htmlFor="emailAddress">
						Email Address <span className="required">*</span>
					</label>
					<input
						type="email"
						id="emailAddress"
						{...form.register("emailAddress")}
						placeholder="e.g., contact@yourcompany.com"
						className={errors.emailAddress ? "error" : undefined}
						aria-invalid={!!errors.emailAddress}
						aria-describedby={
							errors.emailAddress ? "emailAddress-error" : undefined
						}
					/>
					{errors.emailAddress?.message && (
						<FieldError
							id="emailAddress-error"
							message={errors.emailAddress.message}
						/>
					)}
				</div>

				{/* Client Domain / TOML File Domain */}
				<div className="form-row">
					<label htmlFor="clientDomain">
						{walletType === "custodial" ? "Client Domain" : "TOML File Domain"}{" "}
						<span className="required">*</span>
					</label>
					<input
						type="text"
						id="clientDomain"
						{...form.register("clientDomain")}
						placeholder={
							walletType === "custodial"
								? "e.g., api.yourcompany.com"
								: "e.g., stellar.yourcompany.com"
						}
						className={errors.clientDomain ? "error" : undefined}
						aria-invalid={!!errors.clientDomain}
						aria-describedby={
							errors.clientDomain ? "clientDomain-error" : undefined
						}
					/>
					{errors.clientDomain?.message && (
						<FieldError
							id="clientDomain-error"
							message={errors.clientDomain.message}
						/>
					)}
					{walletType === "non-custodial" && (
						<small className="help-text">
							Enter the domain where your <code>stellar.toml</code> file is
							hosted.
							<br />
							<strong>Example:</strong> If your TOML file is at{" "}
							<code>
								https://stellar.yourcompany.com/.well-known/stellar.toml
							</code>
							, enter <code>stellar.yourcompany.com</code>
							<br />
							<a
								href="https://stellar.sui.li/"
								target="_blank"
								rel="noopener noreferrer"
								className="toml-checker-link"
							>
								Validate your TOML file
							</a>
						</small>
					)}
				</div>

				{/* Custodial Wallet Fields */}
				{walletType === "custodial" && (
					<div className="wallet-accounts-section">
						<h4>Wallet Accounts</h4>
						<p className="section-note">
							Stellar public keys (56 characters starting with 'G')
						</p>

						<div className="form-row">
							<label htmlFor="authenticationWalletAccounts">
								Authentication Wallet Account{" "}
								<span className="required">*</span>
							</label>
							<input
								type="text"
								id="authenticationWalletAccounts"
								{...form.register("authenticationWalletAccounts")}
								placeholder="G... (56 characters)"
								className={
									errors.authenticationWalletAccounts ? "error" : undefined
								}
								aria-invalid={!!errors.authenticationWalletAccounts}
								aria-describedby={
									errors.authenticationWalletAccounts
										? "authenticationWalletAccounts-error"
										: undefined
								}
							/>
							{errors.authenticationWalletAccounts?.message && (
								<FieldError
									id="authenticationWalletAccounts-error"
									message={errors.authenticationWalletAccounts.message}
								/>
							)}
						</div>

						<div className="form-row">
							<label htmlFor="withdrawWalletAccounts">
								Withdraw Wallet Account <span className="required">*</span>
							</label>
							<input
								type="text"
								id="withdrawWalletAccounts"
								{...form.register("withdrawWalletAccounts")}
								placeholder="G... (56 characters)"
								className={errors.withdrawWalletAccounts ? "error" : undefined}
								aria-invalid={!!errors.withdrawWalletAccounts}
								aria-describedby={
									errors.withdrawWalletAccounts
										? "withdrawWalletAccounts-error"
										: undefined
								}
							/>
							{errors.withdrawWalletAccounts?.message && (
								<FieldError
									id="withdrawWalletAccounts-error"
									message={errors.withdrawWalletAccounts.message}
								/>
							)}
						</div>

						<div className="form-row">
							<label htmlFor="depositWalletAccounts">
								Deposit Wallet Account <span className="required">*</span>
							</label>
							<input
								type="text"
								id="depositWalletAccounts"
								{...form.register("depositWalletAccounts")}
								placeholder="G... (56 characters)"
								className={errors.depositWalletAccounts ? "error" : undefined}
								aria-invalid={!!errors.depositWalletAccounts}
								aria-describedby={
									errors.depositWalletAccounts
										? "depositWalletAccounts-error"
										: undefined
								}
							/>
							{errors.depositWalletAccounts?.message && (
								<FieldError
									id="depositWalletAccounts-error"
									message={errors.depositWalletAccounts.message}
								/>
							)}
						</div>
					</div>
				)}

				{/* Form Actions */}
				<div className="form-actions">
					<button
						type="button"
						className="btn btn-secondary"
						onClick={onCancel}
						disabled={isSubmitting}
					>
						Cancel
					</button>
					<button
						type="submit"
						className="btn btn-primary"
						disabled={isSubmitting}
					>
						{isSubmitting ? (
							<>
								<span className="spinner-small"></span> Submitting...
							</>
						) : (
							"Submit for Whitelisting"
						)}
					</button>
				</div>
			</form>
		</div>
	);
};

export default WhitelistingForm;
